import { useRef, useEffect } from 'react';
import { Chart, registerables } from 'chart.js';
import { McpToolCallRecord } from 'Core-utils/staticData/staticAgents';
import { Typography } from 'Components/atoms';
import './styles.scss';

Chart.register(...registerables);

// App color palette — matches theme/variables.scss.
// Exported so consumers (e.g. MCP tool chips) can use the same per-service colors.
export const POLAR_COLORS = [
    { bg: 'rgba(153, 117, 255, 0.55)', border: '#9975ff' }, // primary purple
    { bg: 'rgba(161, 215, 48,  0.55)', border: '#a1d730' }, // success green
    { bg: 'rgba(255, 140, 34,  0.55)', border: '#ff8c22' }, // orange
    { bg: 'rgba(241, 223, 58,  0.55)', border: '#f1df3a' }, // yellow
    { bg: 'rgba(221, 58,  58,  0.55)', border: '#dd3a3a' }, // red
    { bg: 'rgba(59,  94,  220, 0.55)', border: '#3b5edc' }, // blue
    { bg: 'rgba(124, 77,  255, 0.55)', border: '#7c4dff' }, // deep purple
    { bg: 'rgba(233, 146, 67,  0.55)', border: '#e99243' }, // amber
];

interface McpPolarAreaChartProps {
    toolCalls: McpToolCallRecord[];
}

const McpPolarAreaChart = ({ toolCalls }: McpPolarAreaChartProps) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        if (!canvasRef.current || !toolCalls?.length) {
            return;
        }

        const ctx = canvasRef.current.getContext('2d');
        if (!ctx) {
            return;
        }

        const labels = toolCalls.map((t) => t.service);
        const data = toolCalls.map((t) => t.count);
        const colors = toolCalls.map((_, i) => POLAR_COLORS[i % POLAR_COLORS.length]);

        const chart = new Chart(ctx, {
            type: 'polarArea',
            data: {
                labels,
                datasets: [
                    {
                        label: 'Tool Calls',
                        data,
                        backgroundColor: colors.map((c) => c.bg),
                        borderColor: colors.map((c) => c.border),
                        borderWidth: 1.5,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: true,
                aspectRatio: 1,
                plugins: {
                    legend: {
                        position: 'right',
                        labels: {
                            color: 'rgba(255,255,255,0.75)',
                            font: { size: 12 },
                            padding: 14,
                            boxWidth: 12,
                        },
                    },
                    tooltip: {
                        callbacks: {
                            label: (ctx) => ` ${ctx.label}: ${ctx.parsed.r.toLocaleString()} calls`,
                        },
                    },
                    title: { display: false },
                },
                scales: {
                    r: {
                        ticks: {
                            color: 'rgba(255,255,255,0.45)',
                            backdropColor: 'transparent',
                            font: { size: 10 },
                        },
                        grid: {
                            color: 'rgba(255,255,255,0.08)',
                        },
                        angleLines: {
                            color: 'rgba(255,255,255,0.08)',
                        },
                    },
                },
            },
        });

        return () => chart.destroy();
    }, [toolCalls]);

    if (!toolCalls?.length) {
        return (
            <div className="mcp-polar-chart__empty d-flex align-items-center justify-content-center h-100">
                <Typography variant="body3" color="text-medium-emphasis">
                    No MCP tool call data available.
                </Typography>
            </div>
        );
    }

    return (
        <div className="mcp-polar-chart d-flex flex-column gap-2 h-100">
            <Typography variant="subtitle2" color="text-high-emphasis">
                MCP Tool Calls
            </Typography>
            <div className="mcp-polar-chart__canvas-wrapper d-flex align-items-center justify-content-center flex-grow-1">
                <canvas ref={canvasRef} />
            </div>
        </div>
    );
};

export default McpPolarAreaChart;
